import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { promoOffers, type PromoOffer } from '@/mocks/promos';

const useCountdown = (endsAt: string) => {
  const calc = () => {
    const diff = Math.max(0, new Date(endsAt).getTime() - Date.now());
    return {
      h: Math.floor(diff / 3600000),
      m: Math.floor((diff % 3600000) / 60000),
      s: Math.floor((diff % 60000) / 1000),
    };
  };
  const [time, setTime] = useState(calc);
  useEffect(() => {
    const id = setInterval(() => setTime(calc()), 1000);
    return () => clearInterval(id);
  }, [endsAt]);
  return time;
};

const pad = (n: number) => String(n).padStart(2, '0');

const TimeUnit = ({ value, label, color }: { value: number; label: string; color: string }) => (
  <div className="flex flex-col items-center">
    <div
      className="font-orbitron font-black text-xl md:text-2xl leading-none px-2.5 py-1.5 rounded-xl min-w-[48px] text-center"
      style={{ background: `${color}15`, border: `1px solid ${color}35`, color, textShadow: `0 0 12px ${color}60` }}
    >
      {pad(value)}
    </div>
    <span className="font-mono text-[9px] text-white/30 mt-1 uppercase tracking-widest">{label}</span>
  </div>
);

const Separator = ({ color }: { color: string }) => (
  <span className="font-orbitron font-black text-xl pb-4" style={{ color, opacity: 0.5 }}>:</span>
);

interface CatalogPromoBannerProps {
  className?: string;
}

const CatalogPromoBanner = ({ className = '' }: CatalogPromoBannerProps) => {
  const [activeIdx, setActiveIdx] = useState(0);
  const [animating, setAnimating] = useState(false);
  const [copied, setCopied] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const offer: PromoOffer = promoOffers[activeIdx];
  const { h, m, s } = useCountdown(offer.endsAt);

  const goTo = (idx: number) => {
    if (idx === activeIdx || animating) return;
    setAnimating(true);
    setTimeout(() => {
      setActiveIdx(idx);
      setAnimating(false);
    }, 280);
  };

  // Auto-rotate every 6s
  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setActiveIdx(i => (i + 1) % promoOffers.length);
    }, 6000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, []);

  const resetTimer = (idx: number) => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    goTo(idx);
    intervalRef.current = setInterval(() => {
      setActiveIdx(i => (i + 1) % promoOffers.length);
    }, 6000);
  };

  const handleCopy = () => {
    if (!offer.code) return;
    navigator.clipboard.writeText(offer.code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className={`relative rounded-2xl overflow-hidden ${className}`} style={{ border: `1px solid ${offer.color}25` }}>

      {/* BG image */}
      <div
        className="absolute inset-0 transition-opacity duration-500"
        style={{ opacity: animating ? 0 : 1 }}
      >
        <img
          src={offer.image}
          alt={offer.title}
          className="w-full h-full object-cover object-top"
        />
        {/* Dark overlay */}
        <div className="absolute inset-0" style={{ background: 'linear-gradient(105deg, rgba(5,5,8,0.97) 0%, rgba(5,5,8,0.88) 45%, rgba(5,5,8,0.55) 100%)' }} />
      </div>

      {/* Animated glow orbs */}
      <div
        className="absolute -top-10 -left-10 w-48 h-48 rounded-full pointer-events-none"
        style={{ background: offer.color, filter: 'blur(70px)', opacity: 0.12, animation: 'orbPulse 3s ease-in-out infinite' }}
      />
      <div
        className="absolute -bottom-10 right-1/3 w-40 h-40 rounded-full pointer-events-none"
        style={{ background: offer.colorSecondary, filter: 'blur(60px)', opacity: 0.1, animation: 'orbPulse 4s ease-in-out infinite reverse' }}
      />

      {/* Scanlines */}
      <div
        className="absolute inset-0 pointer-events-none opacity-20"
        style={{ background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,255,255,0.012) 2px, rgba(255,255,255,0.012) 4px)' }}
      />

      {/* Corner accents */}
      {['top-0 left-0', 'top-0 right-0', 'bottom-0 left-0', 'bottom-0 right-0'].map((pos, i) => (
        <div key={i} className={`absolute ${pos} w-6 h-6 z-10 pointer-events-none`}>
          <div className="absolute inset-0 transition-colors duration-500" style={{
            borderTop: i < 2 ? `1px solid ${offer.color}60` : 'none',
            borderBottom: i >= 2 ? `1px solid ${offer.color}60` : 'none',
            borderLeft: i % 2 === 0 ? `1px solid ${offer.color}60` : 'none',
            borderRight: i % 2 === 1 ? `1px solid ${offer.color}60` : 'none',
          }} />
        </div>
      ))}

      {/* Content */}
      <div
        className="relative z-10 flex flex-col md:flex-row items-start md:items-center gap-6 p-5 md:p-6 transition-all duration-300"
        style={{ opacity: animating ? 0 : 1, transform: animating ? 'translateX(-12px)' : 'translateX(0)' }}
      >
        {/* Left: badge + title + desc */}
        <div className="flex-1 min-w-0">
          {/* Badge */}
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <span
              className="flex items-center gap-1.5 px-3 py-1 rounded-full font-mono text-[10px] font-bold tracking-wider"
              style={{ background: `${offer.color}20`, border: `1px solid ${offer.color}50`, color: offer.color }}
            >
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: offer.color, animation: 'blink 1.2s ease-in-out infinite' }} />
              {offer.badge}
            </span>
            <span
              className="font-orbitron font-black text-2xl md:text-3xl px-3 py-0.5 rounded-xl"
              style={{ background: `${offer.color}18`, border: `1px solid ${offer.color}40`, color: offer.color, textShadow: `0 0 20px ${offer.color}60` }}
            >
              {offer.discount}
            </span>
          </div>

          <h3 className="font-orbitron font-black text-base md:text-lg text-white leading-tight mb-1.5">
            {offer.title}
          </h3>
          <p className="font-mono text-xs text-white/45 leading-relaxed mb-3 max-w-sm line-clamp-2">
            {offer.description}
          </p>

          {/* Promo code */}
          {offer.code && (
            <button
              onClick={handleCopy}
              className="whitespace-nowrap flex items-center gap-2 px-3 py-2 rounded-xl font-mono text-xs cursor-pointer transition-all duration-200 group"
              style={copied
                ? { background: 'rgba(57,255,20,0.12)', border: '1px solid rgba(57,255,20,0.4)', color: '#39ff14' }
                : { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.5)' }
              }
            >
              <i className={copied ? 'ri-check-line' : 'ri-coupon-3-line'} style={{ color: copied ? '#39ff14' : offer.color }} />
              <span className="text-white/30">Промокод:</span>
              <span className="font-bold tracking-widest" style={{ color: offer.color }}>{offer.code}</span>
              <i className={`ml-1 transition-opacity ${copied ? 'ri-check-double-line opacity-100' : 'ri-file-copy-line opacity-0 group-hover:opacity-60'}`} style={{ fontSize: '11px' }} />
            </button>
          )}
        </div>

        {/* Center: countdown */}
        <div className="flex flex-col items-center gap-2 shrink-0">
          <span className="font-mono text-[9px] text-white/30 uppercase tracking-widest">Акция заканчивается через</span>
          <div className="flex items-end gap-1.5">
            <TimeUnit value={h} label="часов" color={offer.color} />
            <Separator color={offer.color} />
            <TimeUnit value={m} label="минут" color={offer.color} />
            <Separator color={offer.color} />
            <TimeUnit value={s} label="секунд" color={offer.color} />
          </div>
        </div>

        {/* Right: CTA */}
        <div className="flex flex-col items-stretch gap-2 shrink-0 w-full md:w-auto">
          <Link
            to="/promos"
            className="whitespace-nowrap flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-mono text-sm font-bold cursor-pointer transition-all duration-200"
            style={{ background: offer.color, color: '#050508', boxShadow: `0 0 20px ${offer.color}40` }}
            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.boxShadow = `0 0 30px ${offer.color}70`; }}
            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.boxShadow = `0 0 20px ${offer.color}40`; }}
          >
            <i className="ri-arrow-right-circle-line" />
            Смотреть акцию
          </Link>
          <Link
            to="/catalog"
            className="whitespace-nowrap flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl font-mono text-xs cursor-pointer transition-all duration-200"
            style={{ border: `1px solid ${offer.color}30`, color: `${offer.color}90` }}
            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = `${offer.color}08`; }}
            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
          >
            <i className="ri-price-tag-3-line" />
            Все акции
          </Link>
        </div>
      </div>

      {/* Slide dots */}
      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-2 z-20">
        {promoOffers.map((_, i) => (
          <button
            key={i}
            onClick={() => resetTimer(i)}
            className="rounded-full cursor-pointer transition-all duration-300"
            style={{
              width: activeIdx === i ? '20px' : '6px',
              height: '6px',
              background: activeIdx === i ? offer.color : 'rgba(255,255,255,0.2)',
              boxShadow: activeIdx === i ? `0 0 8px ${offer.color}` : 'none',
            }}
          />
        ))}
      </div>

      {/* Progress bar */}
      <div className="absolute bottom-0 left-0 right-0 h-0.5" style={{ background: 'rgba(255,255,255,0.05)' }}>
        <div
          className="h-full rounded-full"
          style={{
            background: `linear-gradient(90deg, ${offer.color}, ${offer.colorSecondary})`,
            animation: 'progressBar 6s linear infinite',
            boxShadow: `0 0 6px ${offer.color}`,
          }}
        />
      </div>

      <style>{`
        @keyframes orbPulse {
          0%, 100% { transform: scale(1); opacity: 0.12; }
          50% { transform: scale(1.15); opacity: 0.18; }
        }
        @keyframes blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
        @keyframes progressBar {
          from { width: 0%; }
          to { width: 100%; }
        }
      `}</style>
    </div>
  );
};

export default CatalogPromoBanner;
